import {
  createContext, useContext, useRef, useState, useCallback, useEffect, ReactNode,
} from 'react';
import { useSelector } from 'react-redux';
import {
  actOnCall,
  actOnCallKeepalive,
  createCallEventId,
  getCallSnapshot,
  getIceServers,
  getP2pOffer,
  logCall,
  logCallEvent,
  logCallKeepalive,
  startCall as startAuthoritativeCall,
  syncCalls,
  CallLogCreate,
  CallOutcome,
} from '../api/calls';
import type {
  CallEventEnvelope,
  CallLifecycleAction,
  CallSnapshot,
} from '../types/calls';
import { setCallActive, isOtherCallActive } from '../utils/call/callBusy';
import { deriveOutcome } from '../utils/call/callLog';
import { SpeakingMonitor } from '../utils/call/speaking';
import { bumpMissed } from '../utils/call/missedBadge';
import { getConversation } from '../api/chat';
import { clearNativeIncomingCall, getPendingNativeCall } from '../utils/mobileIncomingCall';
import { PeerService } from '../webrtc/PeerService';
import CallScreen from '../components/call/CallScreen';
import StartCallPicker from '../components/calls/StartCallPicker';
import { useDndQuietMode } from './DndQuietModeContext';
import {
  startRingtone, startRingback, stopAllCallSounds, playConnectTone, playEndTone,
  setSystemRingtone,
} from '../utils/call/callSounds';
import {
  getSystemRingtone, showCallNotification, closeCallNotification,
  onCallNotificationAction, showDesktopNotification,
} from '../utils/electron';
import {
  selectProtectIpAddress, selectSelectedAudioOutputId, selectAudioMutedByDefault,
} from '../redux/slices/chatPreferences';

export type CallStatus =
  | 'idle' | 'outgoing' | 'incoming' | 'connecting' | 'connected' | 'ended';

export interface CallState {
  status: CallStatus;
  callId: string | null;
  conversationId: string | null;
  peerUserId: string | null;
  peerName: string | null;
  peerAvatarUrl: string | null;
  muted: boolean;
  startedAt: number | null;
  endReason: string | null;
}

const IDLE: CallState = {
  status: 'idle', callId: null, conversationId: null, peerUserId: null,
  peerName: null, peerAvatarUrl: null, muted: false, startedAt: null, endReason: null,
};

/** Auto-end a call that rings unanswered this long (typical no-answer timeout). */
// A high-priority push can still take several seconds to wake a sleeping
// mobile recipient. Leave enough time for the recipient to actually answer.
const RING_TIMEOUT_MS = 90_000;

/**
 * Media must connect this soon after a call goes active. Before the backend
 * relayed answers after a v2 accept, callers sat in "connecting" forever.
 */
export const CONNECT_WATCHDOG_MS = 30_000;

/**
 * Grace period before tearing down a call that bounced to 'disconnected'.
 * WebRTC commonly flaps a healthy connection to 'disconnected' on a brief
 * network blip / Wi-Fi handoff and then recovers to 'connected' on its own.
 * Only tear down if it has NOT recovered (or moved to 'failed') by the time
 * this elapses — so a momentary hiccup can't kill an otherwise-fine call.
 */
const DISCONNECT_GRACE_MS = 6_000;

function terminalActionFor(status: CallStatus): CallLifecycleAction {
  if (status === 'outgoing') return 'cancel';
  if (status === 'incoming') return 'decline';
  return 'end';
}

function isTerminalSnapshot(status: CallSnapshot['status']): boolean {
  return status === 'ended' || status === 'declined' || status === 'cancelled' || status === 'missed';
}

function isAuthorizedRingingInvite(frame: any, authorized: CallSnapshot): boolean {
  return authorized.callId === frame.call_id
    && authorized.conversationId === frame.conversation_id
    && authorized.initiatorUserId === frame.from_user_id
    && authorized.participantUserIds.includes(frame.from_user_id)
    && authorized.mediaTopology === 'p2p'
    && !authorized.isGroup
    && authorized.status === 'ringing';
}

type SendFn = (frame: Record<string, any>) => void;

interface CallApi {
  call: CallState;
  snapshot: CallSnapshot | null;
  /** Whether the active call is collapsed to the floating pill. */
  minimized: boolean;
  startCall: (conversationId: string, peerUserId: string, peerName?: string | null, peerAvatarUrl?: string | null) => Promise<void>;
  accept: () => Promise<void>;
  reject: () => void;
  hangup: () => void;
  toggleMute: () => void;
  toggleMinimize: () => void;
  minimize: () => void;
  /** Called by the WS layer to deliver an inbound call-* frame. */
  handleSignal: (frame: any) => void;
  /** Called by the WS layer to deliver an authoritative lifecycle frame. */
  handleCallState: (frame: CallEventEnvelope) => void;
  /** Reconcile persisted calls after initial load or WebSocket reconnect. */
  recoverCalls: () => Promise<void>;
  /** Called by the WS layer to register the outbound send function. */
  bindSend: (fn: SendFn) => void;
  /** Register the 1:1→group escalation handler (provided by CallEscalationBridge). */
  bindEscalate: (fn: (args: { peerUserId: string; peerName: string | null; newUserIds: string[] }) => void) => void;
}

const Ctx = createContext<CallApi | null>(null);

function useOptionalPreference<T>(selector: (state: any) => T, fallback: T): T {
  // Some embedded call consumers (and the focused provider tests) mount the
  // provider outside the app Redux shell. Call preferences are additive, so
  // those surfaces retain safe defaults instead of making calling unusable.
  try { return useSelector(selector); } catch { return fallback; }
}

export function CallProvider({ children }: { children: ReactNode }) {
  const { isDndQuiet } = useDndQuietMode();
  const protectIpAddress = useOptionalPreference(selectProtectIpAddress, false);
  const selectedAudioOutputId = useOptionalPreference(selectSelectedAudioOutputId, null);
  const audioMutedByDefault = useOptionalPreference(selectAudioMutedByDefault, false);
  const [call, setCall] = useState<CallState>(IDLE);
  const [snapshot, setSnapshot] = useState<CallSnapshot | null>(null);
  const [minimized, setMinimized] = useState(false);
  const [addingOpen, setAddingOpen] = useState(false);
  const [remoteSpeaking, setRemoteSpeaking] = useState(false);
  const escalateRef = useRef<((args: { peerUserId: string; peerName: string | null; newUserIds: string[] }) => void) | null>(null);
  const peerRef = useRef<PeerService | null>(null);
  const snapshotRef = useRef<CallSnapshot | null>(null);
  const startingCallRef = useRef(false);
  const pendingActionRef = useRef<{ callId: string; eventId: string } | null>(null);
  const recoveryInFlightRef = useRef<Promise<void> | null>(null);
  const unloadActionRef = useRef<{ callId: string; eventId: string } | null>(null);
  const sendRef = useRef<SendFn>(() => {});
  const remoteAudioRef = useRef<HTMLAudioElement | null>(null);
  // Detects when the peer is talking → drives the avatar speaking wave.
  const speakingRef = useRef<SpeakingMonitor | null>(null);
  // Pending grace timer for a transient 'disconnected'. Armed when the peer
  // connection flaps to 'disconnected', and cleared the moment it recovers to
  // 'connected', moves to 'failed'/'closed', or the call tears down.
  const disconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Buffer for ICE candidates that arrive before the PeerService exists
  // (callee receives candidates before pressing Accept).
  const pendingCandidates = useRef<RTCIceCandidateInit[]>([]);
  const pendingOffer = useRef<RTCSessionDescriptionInit | null>(null);
  // Mirror of the live call routing info for use inside callbacks.
  const routeRef = useRef<{ callId: string; conversationId: string; peerUserId: string } | null>(null);
  // Only the caller applies a remote answer, and only once — guards against a
  // self-call (same account in two windows) or a duplicate frame, which would
  // otherwise throw "setRemoteDescription ... wrong state: stable".
  const isCallerRef = useRef(false);
  const answerAppliedRef = useRef(false);
  // Caller's own gathered ICE candidates. Candidates relayed while the callee's
  // socket was still offline (killed mobile app answering from a push) are
  // dropped, so they are replayed once the answer proves the callee is online.
  const localCandidatesRef = useRef<any[]>([]);
  // Always-current mirror of call.status for use in unmount/unload handlers
  // whose effect runs only once (stable deps) and would otherwise see a stale
  // status from the closure.
  const statusRef = useRef(call.status);
  statusRef.current = call.status;
  // Cross-tab coordination: the backend fans a call-invite out to EVERY socket
  // the user has open, so each tab rings independently with its own audio state.
  // A reject only goes to the caller, never the user's other tabs — so without
  // this they'd ring on until their own 45s timeout. We broadcast a resolution
  // on this channel so sibling tabs dismiss the same call at once.
  const callChannelRef = useRef<BroadcastChannel | null>(null);
  // call_ids we've already resolved (declined/ended), so a duplicate or
  // re-published call-invite for the same id can't re-ring. Bounded FIFO.
  const declinedCallIdsRef = useRef<string[]>([]);
  // Snapshot of the live call's identity/timing, captured at start/invite and
  // updated on connect, so logCallOutcome() can build a record even as cleanup
  // tears the rest down. Direction is from this side's perspective.
  const callMetaRef = useRef<{
    callId: string; peerUserId: string; conversationId: string;
    direction: 'incoming' | 'outgoing'; ringStartedAt: number; answeredAt: number | null;
  } | null>(null);
  // call_ids already logged by THIS client, so cleanup + the unload beacon
  // can't double-post the same side. Bounded FIFO.
  const loggedCallIdsRef = useRef<string[]>([]);
  const eventLoggedRef = useRef<string[]>([]);
  const nativeCallIdRef = useRef<string | null>(null);

  const bindSend = useCallback((fn: SendFn) => { sendRef.current = fn; }, []);

  const bindEscalate = useCallback((fn: (args: { peerUserId: string; peerName: string | null; newUserIds: string[] }) => void) => {
    escalateRef.current = fn;
  }, []);

  const sendFrame = useCallback((type: string, extra: Record<string, any> = {}) => {
    const r = routeRef.current;
    if (!r) return;
    sendRef.current({
      type, call_id: r.callId, conversation_id: r.conversationId, to_user_id: r.peerUserId, ...extra,
    });
  }, []);

  const logCallOutcome = useCallback((outcome: CallOutcome) => {
    const meta = callMetaRef.current;
    if (!meta) return;
    if (loggedCallIdsRef.current.includes(meta.callId)) return;
    loggedCallIdsRef.current.push(meta.callId);
    if (loggedCallIdsRef.current.length > 50) loggedCallIdsRef.current.shift();
    const endedAt = Date.now();
    const payload: CallLogCreate = {
      call_id: meta.callId,
      peer_user_id: meta.peerUserId,
      conversation_id: meta.conversationId,
      direction: meta.direction,
      call_type: 'voice',
      outcome,
      started_at: new Date(meta.ringStartedAt).toISOString(),
      answered_at: meta.answeredAt ? new Date(meta.answeredAt).toISOString() : null,
      ended_at: new Date(endedAt).toISOString(),
      duration_s: meta.answeredAt ? Math.max(0, Math.round((endedAt - meta.answeredAt) / 1000)) : 0,
    };
    void logCall(payload).catch(() => { /* best-effort; peer's row is independent */ });
    if (meta.direction === 'incoming' && outcome === 'missed') bumpMissed();
  }, []);

  const cleanup = useCallback((
    reason: string | null,
    broadcast = true,
    outcome: CallOutcome | 'none' | 'derive' = 'derive',
  ) => {
    // Stop the ring SYNCHRONOUSLY here — cleanup() is the single choke point
    // every teardown (reject/hangup/cancel/end/timeout/connection-lost) passes
    // through. Relying on the status-driven useEffect proved unreliable: on a
    // decline the status went incoming→idle and the overlay closed, but the
    // effect did not always run stopAllCallSounds, so the loop kept ringing
    // until the 45s timeout. A direct call here can never be skipped.
    stopAllCallSounds();
    setCallActive('oneToOne', false);
    // Log this side's perspective exactly once, before the meta is cleared.
    const meta = callMetaRef.current;
    const resolved: CallOutcome | 'none' =
      outcome === 'derive' ? deriveOutcome(reason, meta?.answeredAt != null) : outcome;
    if (resolved !== 'none') logCallOutcome(resolved);
    // Shared in-thread call_event — caller side only (owns the call_id origin).
    // Idempotent server-side on call_id, so a retry/echo is harmless.
    if (meta && isCallerRef.current && resolved !== 'none' && !eventLoggedRef.current.includes(meta.callId)) {
      eventLoggedRef.current.push(meta.callId);
      const endedAt = Date.now();
      void logCallEvent({
        call_id: meta.callId,
        conversation_id: meta.conversationId,
        call_type: 'voice',
        outcome: resolved,
        is_group: false,
        duration_s: meta.answeredAt ? Math.max(0, Math.round((endedAt - meta.answeredAt) / 1000)) : 0,
        started_at: new Date(meta.ringStartedAt).toISOString(),
        ended_at: new Date(endedAt).toISOString(),
        peer_user_id: meta.peerUserId,
      }).catch(() => {});
    }
    const resolvedId = routeRef.current?.callId;
    if (resolvedId) {
      // Remember it so a duplicate/re-published invite for this id can't re-ring.
      const seen = declinedCallIdsRef.current;
      if (!seen.includes(resolvedId)) {
        seen.push(resolvedId);
        if (seen.length > 50) seen.shift();
      }
      // Tell this user's OTHER tabs (also ringing this same call) to dismiss it.
      // `broadcast=false` when we're already reacting to a sibling's broadcast.
      if (broadcast) {
        try { callChannelRef.current?.postMessage({ type: 'call-resolved', callId: resolvedId }); } catch { /* noop */ }
      }
    }
    // Kill any pending disconnect grace timer so it can never fire after the
    // call has already been torn down.
    if (disconnectTimerRef.current) {
      clearTimeout(disconnectTimerRef.current);
      disconnectTimerRef.current = null;
    }
    peerRef.current?.close();
    peerRef.current = null;
    localCandidatesRef.current = [];
    pendingCandidates.current = [];
    pendingOffer.current = null;
    routeRef.current = null;
    callMetaRef.current = null;
    pendingActionRef.current = null;
    unloadActionRef.current = null;
    isCallerRef.current = false;
    answerAppliedRef.current = false;
    if (remoteAudioRef.current) remoteAudioRef.current.srcObject = null;
    if (nativeCallIdRef.current) {
      const pendingId = nativeCallIdRef.current;
      nativeCallIdRef.current = null;
      void clearNativeIncomingCall(pendingId);
    }
    speakingRef.current?.close();
    speakingRef.current = null;
    setRemoteSpeaking(false);
    setAddingOpen(false);   // never let an open "Add" sheet leak into the next call
    setCall(reason ? { ...IDLE, status: 'ended', endReason: reason } : IDLE);
    if (reason) {
      // brief "ended" flash, then back to idle
      setTimeout(() => { setCall(IDLE); setMinimized(false); }, 1500);
    } else {
      setMinimized(false);
    }
  }, [logCallOutcome]);

  const storeSnapshot = useCallback((next: CallSnapshot): boolean => {
    const previous = snapshotRef.current;
    if (previous?.callId === next.callId && previous.version >= next.version) return false;
    snapshotRef.current = next;
    setSnapshot(next);
    return true;
  }, []);

  const reconcileSnapshot = useCallback((next: CallSnapshot) => {
    if (!storeSnapshot(next)) return;
    if (routeRef.current?.callId !== next.callId) return;

    if (next.status === 'active') {
      if (
        statusRef.current === 'incoming'
        && pendingActionRef.current?.eventId !== next.eventId
      ) {
        // Another device/tab answered this ring. It owns media; this renderer
        // dismisses silently instead of opening a connecting surface with no peer.
        cleanup(null, true, 'none');
        return;
      }
      setCall((current) => current.callId === next.callId && current.status !== 'connected'
        ? { ...current, status: 'connecting' }
        : current);
      return;
    }
    if (next.status === 'ringing') return;
    if (pendingActionRef.current?.callId === next.callId && pendingActionRef.current.eventId === next.eventId) {
      return;
    }

    const reason = next.status === 'declined'
      ? 'Call declined'
      : next.status === 'cancelled'
        ? 'Call cancelled'
      : next.status === 'missed'
        ? callMetaRef.current?.direction === 'incoming' ? 'Missed call' : 'No answer'
        : 'Call ended';
    cleanup(reason);
  }, [cleanup, storeSnapshot]);

  const handleCallState = useCallback((event: CallEventEnvelope) => {
    if (
      event.callId !== event.snapshot.callId
      || event.sequenceNumber !== event.snapshot.version
      || event.eventId !== event.snapshot.eventId
    ) return;

    const previous = snapshotRef.current;
    if (previous?.callId === event.callId && event.snapshot.version > previous.version + 1) {
      void getCallSnapshot(event.callId).then(reconcileSnapshot).catch(() => { /* next state frame retries */ });
      return;
    }
    reconcileSnapshot(event.snapshot);
  }, [reconcileSnapshot]);

  const applyAuthoritativeAction = useCallback(async (
    action: CallLifecycleAction,
  ): Promise<CallSnapshot | null> => {
    const route = routeRef.current;
    if (!route) return null;

    let current = snapshotRef.current?.callId === route.callId ? snapshotRef.current : null;
    const eventId = createCallEventId(`call-${action}`);
    try {
      current ??= await getCallSnapshot(route.callId);
      if (current.callId !== route.callId) return null;
      storeSnapshot(current);
      if (isTerminalSnapshot(current.status)) {
        return current;
      }

      pendingActionRef.current = { callId: route.callId, eventId };
      const next = await actOnCall(route.callId, {
        action, eventId, expectedVersion: current.version,
      });
      storeSnapshot(next);
      return next.callId === route.callId ? next : null;
    } catch (error: any) {
      if (error?.response?.status !== 409) return null;
      try {
        const latest = await getCallSnapshot(route.callId);
        if (latest.callId !== route.callId) return null;
        storeSnapshot(latest);
        const terminal = isTerminalSnapshot(latest.status);
        if (terminal || (action === 'answer' && latest.status === 'active')) return latest;
        pendingActionRef.current = { callId: route.callId, eventId };
        const retried = await actOnCall(route.callId, {
          action, eventId, expectedVersion: latest.version,
        });
        storeSnapshot(retried);
        return retried.callId === route.callId ? retried : null;
      } catch {
        return null;
      }
    } finally {
      if (pendingActionRef.current?.eventId === eventId) pendingActionRef.current = null;
    }
  }, [storeSnapshot]);

  const wirePeer = useCallback((peer: PeerService) => {
    peer.onIceCandidate = (candidate) => {
      if (isCallerRef.current) localCandidatesRef.current.push(candidate);
      sendFrame('ice-candidate', { candidate });
    };
    peer.onRemoteStream = (stream) => {
      if (remoteAudioRef.current) {
        remoteAudioRef.current.srcObject = stream;
        void remoteAudioRef.current.play().catch(() => {});
        void peer.setOutputSink(remoteAudioRef.current, selectedAudioOutputId);
      }
      // Watch the peer's loudness so their avatar shows a speaking wave.
      if (!speakingRef.current) {
        speakingRef.current = new SpeakingMonitor((_uid, speaking) => setRemoteSpeaking(speaking));
      }
      speakingRef.current.add('peer', stream);
    };
    peer.onConnectionStateChange = (state) => {
      if (state === 'connected') {
        // Connected (or recovered from a transient blip): cancel any pending
        // disconnect grace timer so it can't tear the live call down.
        if (disconnectTimerRef.current) {
          clearTimeout(disconnectTimerRef.current);
          disconnectTimerRef.current = null;
        }
        if (callMetaRef.current && callMetaRef.current.answeredAt == null) {
          callMetaRef.current.answeredAt = Date.now();
        }
        setCall((c) => (c.status === 'connected' ? c : { ...c, status: 'connected', startedAt: Date.now() }));
        void applyAuthoritativeAction('media-connected');
      } else if (state === 'failed' || state === 'closed') {
        // A non-recovering drop — terminal, tear down at once.
        if (disconnectTimerRef.current) {
          clearTimeout(disconnectTimerRef.current);
          disconnectTimerRef.current = null;
        }
        if (routeRef.current) {
          void applyAuthoritativeAction('media-disconnected');
          const action = terminalActionFor(statusRef.current);
          void applyAuthoritativeAction(action).then(() => cleanup('Connection lost'));
        }
      } else if (state === 'disconnected') {
        // 'disconnected' is frequently TRANSIENT — a brief network blip or
        // Wi-Fi handoff commonly bounces a healthy call here and it recovers
        // on its own within a few seconds. Don't kill the call immediately;
        // arm a short grace timer instead. A return to 'connected' cancels it
        // above; a move to 'failed'/'closed' tears down at once; only a
        // disconnect that never recovers reaches this callback and tears down.
        // Leave the UI on 'connected' meanwhile so a momentary blip doesn't
        // flicker the call screen.
        if (!disconnectTimerRef.current) {
          void applyAuthoritativeAction('media-reconnecting');
          disconnectTimerRef.current = setTimeout(() => {
            disconnectTimerRef.current = null;
            if (routeRef.current) {
              const action = terminalActionFor(statusRef.current);
              void applyAuthoritativeAction(action).then(() => cleanup('Connection lost'));
            }
          }, DISCONNECT_GRACE_MS);
        }
      }
    };
  }, [sendFrame, cleanup, applyAuthoritativeAction, selectedAudioOutputId]);

  const flushCandidates = useCallback(async () => {
    const peer = peerRef.current;
    if (!peer) return;
    const queued = pendingCandidates.current;
    pendingCandidates.current = [];
    for (const c of queued) await peer.addIceCandidate(c);
  }, []);

  const startCall = useCallback(async (
    conversationId: string,
    peerUserId: string,
    peerName: string | null = null,
    peerAvatarUrl: string | null = null,
  ) => {
    if (call.status !== 'idle' || startingCallRef.current || isOtherCallActive('oneToOne')) return;
    startingCallRef.current = true;
    let authoritative: CallSnapshot;
    try {
      authoritative = await startAuthoritativeCall({
        clientEventId: createCallEventId('call-start'),
        conversationId,
        participantUserIds: [peerUserId],
        callType: 'voice',
      });
    } catch {
      return;
    } finally {
      startingCallRef.current = false;
    }
    if (
      authoritative.status !== 'ringing'
      || authoritative.conversationId !== conversationId
      || authoritative.mediaTopology !== 'p2p'
      || authoritative.isGroup
      || !authoritative.participantUserIds.includes(peerUserId)
    ) return;

    const callId = authoritative.callId;
    storeSnapshot(authoritative);
    setCallActive('oneToOne', true);
    routeRef.current = { callId, conversationId, peerUserId };
    unloadActionRef.current = null;
    callMetaRef.current = {
      callId, peerUserId, conversationId,
      direction: 'outgoing', ringStartedAt: Date.now(), answeredAt: null,
    };
    isCallerRef.current = true;
    answerAppliedRef.current = false;
    setMinimized(false);
    setCall({ ...IDLE, muted: audioMutedByDefault, status: 'outgoing', callId, conversationId, peerUserId, peerName, peerAvatarUrl });
    try {
      const iceServers = await getIceServers();
      const peer = new PeerService(iceServers, { protectIpAddress });
      peerRef.current = peer;
      wirePeer(peer);
      await peer.initLocalAudio();
      const offer = await peer.createOffer();
      sendFrame('call-invite', { sdp: offer, media: 'audio' });
    } catch {
      // Mic blocked/denied or SDP setup failed. Without this, status stays
      // 'outgoing' forever and the ringback loop never stops.
      void applyAuthoritativeAction('cancel').then(() => cleanup('Microphone unavailable'));
    }
  }, [call.status, wirePeer, sendFrame, cleanup, storeSnapshot, applyAuthoritativeAction, protectIpAddress, audioMutedByDefault]);

  const accept = useCallback(async () => {
    if (call.status !== 'incoming' || !routeRef.current || isOtherCallActive('oneToOne')) return;
    const accepted = await applyAuthoritativeAction('answer');
    if (!accepted || accepted.status !== 'active') {
      if (accepted && accepted.status !== 'ringing') {
        const reason = accepted.status === 'missed' ? 'Missed call'
          : accepted.status === 'declined' ? 'Call declined' : 'Call ended';
        cleanup(reason);
      }
      return;
    }
    // Answering marks this account busy so a concurrent group call is refused.
    setCallActive('oneToOne', true);
    // Stop the ringtone SYNCHRONOUSLY (don't wait on the status effect), and
    // tell sibling tabs ringing this same call to dismiss it — we answered here.
    stopAllCallSounds();
    try { callChannelRef.current?.postMessage({ type: 'call-resolved', callId: routeRef.current.callId }); } catch { /* noop */ }
    setCall((c) => ({ ...c, muted: audioMutedByDefault || c.muted, status: 'connecting' }));
    try {
      const iceServers = await getIceServers();
      const peer = new PeerService(iceServers, { protectIpAddress });
      peerRef.current = peer;
      wirePeer(peer);
      await peer.initLocalAudio();
      peer.setMuted(audioMutedByDefault);
      if (pendingOffer.current) await peer.setRemoteDescription(pendingOffer.current);
      await flushCandidates();
      const answer = await peer.createAnswer();
      sendFrame('call-answer', { sdp: answer });
    } catch {
      // Mic blocked/denied or SDP setup failed after Accept. Commit the
      // terminal state so call-state tears down every participant reliably.
      void applyAuthoritativeAction('end').then(() => {
        cleanup('Microphone unavailable');
      });
    }
  }, [call.status, wirePeer, sendFrame, flushCandidates, cleanup, applyAuthoritativeAction, protectIpAddress, audioMutedByDefault]);

  const reject = useCallback(() => {
    if (call.status !== 'incoming') return;
    void applyAuthoritativeAction('decline').then((declined) => {
      if (!declined || (declined.status !== 'declined' && declined.status !== 'missed')) return;
      cleanup(null, true, declined.status === 'missed' ? 'missed' : 'declined');
    });
  }, [call.status, cleanup, applyAuthoritativeAction]);

  const hangup = useCallback(() => {
    if (call.status === 'idle' || call.status === 'ended') return;
    const action = terminalActionFor(call.status);
    void applyAuthoritativeAction(action).then((ended) => {
      if (!ended || !isTerminalSnapshot(ended.status)) return;
      const outcome: CallOutcome =
        call.status === 'outgoing' ? (ended.status === 'missed' ? 'no_answer' : 'cancelled')
          : call.status === 'incoming' ? (ended.status === 'missed' ? 'missed' : 'declined')
            : callMetaRef.current?.answeredAt ? 'completed' : 'failed';
      cleanup(null, true, outcome);
    });
  }, [call.status, cleanup, applyAuthoritativeAction]);

  const toggleMute = useCallback(() => {
    setCall((c) => {
      const muted = !c.muted;
      peerRef.current?.setMuted(muted);
      return { ...c, muted };
    });
  }, []);

  const toggleMinimize = useCallback(() => {
    setMinimized((m) => !m);
  }, []);

  const minimize = useCallback(() => {
    setMinimized(true);
  }, []);

  // Drive call sounds straight off the state machine, so loops always start and
  // stop with the call and can never leak. Ringtone (incoming) and ringback
  // (outgoing) loop; connect/end are one-shots. All playback is best-effort.
  useEffect(() => {
    switch (call.status) {
      case 'incoming': startRingtone(); break;
      case 'outgoing': startRingback(); break;
      case 'connected': stopAllCallSounds(); playConnectTone(); break;
      case 'ended': stopAllCallSounds(); playEndTone(); break;
      default: stopAllCallSounds(); break; // idle, connecting
    }
  }, [call.status]);

  // Safety net: stop every loop if the provider unmounts mid-call.
  useEffect(() => () => stopAllCallSounds(), []);

  // Desktop app: ring with the OS ringtone (macOS FaceTime tone / Windows
  // phone ring) instead of the synthesized bell. Best-effort — outside
  // Electron or on any failure the synthesized ringtone stays.
  useEffect(() => {
    let cancelled = false;
    void getSystemRingtone().then((tone) => {
      if (!cancelled && tone) setSystemRingtone(tone.data);
    });
    return () => { cancelled = true; };
  }, []);

  // Surface a ringing call as an always-on-top desktop popup (WhatsApp-style
  // call banner with Accept/Decline), so it's seen — and answerable — while
  // the window is hidden in the tray or behind other apps. The renderer
  // always notifies; MAIN decides visibility (popup suppressed while the
  // window is focused — the CallScreen is the ring UI there — and synced to
  // focus/blur for the whole ring). Withdrawn the moment the call leaves
  // 'incoming' (answered here or in a sibling tab, declined, cancelled,
  // timed out). If it ended UNANSWERED, leave a regular "missed call"
  // notification that deep-links into the conversation.
  const notifiedCallRef = useRef<{
    callId: string; peerName: string | null; conversationId: string | null; avatarUrl: string | null;
  } | null>(null);
  useEffect(() => {
    if (call.status === 'incoming' && call.callId) {
      // One popup per ring — but when the avatar back-fill resolves a photo
      // for the SAME call, notify again: main patches the live popup in
      // place (never re-creates it), so the photo pops in without a flash.
      const avatarUrl = call.peerAvatarUrl ?? null;
      const prev = notifiedCallRef.current;
      if (prev?.callId === call.callId && prev.avatarUrl === avatarUrl) return;
      notifiedCallRef.current = {
        callId: call.callId, peerName: call.peerName, conversationId: call.conversationId, avatarUrl,
      };
      void showCallNotification({
        callId: call.callId,
        title: call.peerName || 'Incoming call',
        body: 'TeamLio audio call',
        iconUrl: avatarUrl,
      });
      return;
    }
    const notified = notifiedCallRef.current;
    if (!notified) return;
    notifiedCallRef.current = null;
    void closeCallNotification(notified.callId);
    // cleanup() reasons for a ring that died unanswered: 45s timeout ('Missed
    // call') or the caller hung up while we rang ('Call cancelled').
    if (call.status === 'ended' && (call.endReason === 'Missed call' || call.endReason === 'Call cancelled')) {
      void showDesktopNotification({
        title: notified.peerName || 'TeamLio',
        body: 'Missed voice call',
        conversationId: notified.conversationId,
      });
    }
  }, [call.status, call.callId, call.peerName, call.peerAvatarUrl, call.conversationId, call.endReason]);

  // Mirror of the sounds safety net: if this renderer goes away mid-ring
  // (provider unmount, reload, auto-update relaunch), withdraw the OS
  // notification too — the fresh renderer has no memory of it and could
  // never close it. Main's TTL is the last-resort backstop.
  useEffect(() => () => {
    if (notifiedCallRef.current) void closeCallNotification(notifiedCallRef.current.callId);
  }, []);

  // Latest accept/reject for the mount-once notification-action subscription
  // below (both callbacks are recreated whenever call.status changes).
  const acceptRef = useRef(accept);
  acceptRef.current = accept;
  const rejectRef = useRef(reject);
  rejectRef.current = reject;

  // React to the notification's buttons: Accept answers right away (main has
  // already focused the window), Decline rejects. A plain body click or
  // dismiss needs nothing here — the ringing CallScreen is the UI.
  useEffect(() => {
    return onCallNotificationAction(({ callId, action }) => {
      if (!callId || routeRef.current?.callId !== callId) return;
      if (statusRef.current !== 'incoming') return;
      if (action === 'accept') void acceptRef.current();
      else if (action === 'decline') rejectRef.current();
    });
  }, []);

  // Hard timeout for the ringing states. Guarantees the ring can NEVER loop
  // forever if the authoritative teardown is delayed — e.g. the caller lost
  // connectivity while closing/refreshing. Re-runs on every
  // status change, so it only counts down while actually ringing.
  useEffect(() => {
    if (call.status !== 'incoming' && call.status !== 'outgoing') return;
    const wasOutgoing = call.status === 'outgoing';
    const id = setTimeout(() => {
      void applyAuthoritativeAction(terminalActionFor(call.status)).then((terminal) => {
        if (!terminal || !isTerminalSnapshot(terminal.status)) return;
        cleanup(
          wasOutgoing ? 'No answer' : 'Missed call',
          true,
          wasOutgoing ? 'no_answer' : 'missed',
        );
      });
    }, RING_TIMEOUT_MS);
    return () => clearTimeout(id);
  }, [call.status, cleanup, applyAuthoritativeAction]);

  useEffect(() => {
    if (call.status !== 'connecting') return;
    const id = setTimeout(() => {
      if (statusRef.current !== 'connecting' || !routeRef.current) return;
      void applyAuthoritativeAction('media-disconnected').then(() => {
        cleanup('Call failed', true, 'failed');
      });
    }, CONNECT_WATCHDOG_MS);
    return () => clearTimeout(id);
  }, [call.status, cleanup, applyAuthoritativeAction]);

  // Persist lifecycle state when this renderer goes away. Terminal direct
  // frames are intentionally rejected by the API; call-state is the durable
  // cross-client terminal source, so unload uses authenticated keepalive fetch.
  useEffect(() => {
    const notifyPeer = () => {
      const s = statusRef.current;
      if (s === 'idle' || s === 'ended') return;
      const route = routeRef.current;
      const authoritative = snapshotRef.current;
      if (
        route
        && authoritative?.callId === route.callId
        && (authoritative.status === 'ringing' || authoritative.status === 'active')
        && unloadActionRef.current?.callId !== route.callId
      ) {
        const eventId = createCallEventId('call-unload');
        unloadActionRef.current = { callId: route.callId, eventId };
        actOnCallKeepalive(route.callId, {
          action: authoritative.status === 'active' ? 'end' : isCallerRef.current ? 'cancel' : 'decline',
          eventId,
          expectedVersion: authoritative.version,
        });
      }
      // Kill local audio synchronously — don't wait for React's unmount effect,
      // or the ring can keep firing for up to one interval while the tab closes.
      stopAllCallSounds();
      // Withdraw the OS call notification too: after the unload nothing else
      // remembers it (fire-and-forget IPC is fine during unload).
      if (notifiedCallRef.current) void closeCallNotification(notifiedCallRef.current.callId);
      // Persist THIS side's row even as the tab closes (keepalive fetch
      // survives unload; the normal cleanup POST would be aborted).
      const meta = callMetaRef.current;
      if (meta && !loggedCallIdsRef.current.includes(meta.callId)) {
        loggedCallIdsRef.current.push(meta.callId);
        const outcome: CallOutcome = meta.direction === 'outgoing'
          ? (meta.answeredAt ? 'completed' : 'cancelled')
          : (meta.answeredAt ? 'completed' : 'missed');
        const endedAt = Date.now();
        logCallKeepalive({
          call_id: meta.callId, peer_user_id: meta.peerUserId, conversation_id: meta.conversationId,
          direction: meta.direction, call_type: 'voice', outcome,
          started_at: new Date(meta.ringStartedAt).toISOString(),
          answered_at: meta.answeredAt ? new Date(meta.answeredAt).toISOString() : null,
          ended_at: new Date(endedAt).toISOString(),
          duration_s: meta.answeredAt ? Math.max(0, Math.round((endedAt - meta.answeredAt) / 1000)) : 0,
        });
        if (meta.direction === 'incoming' && outcome === 'missed') bumpMissed();
      }
    };
    window.addEventListener('beforeunload', notifyPeer);
    window.addEventListener('pagehide', notifyPeer);
    return () => {
      window.removeEventListener('beforeunload', notifyPeer);
      window.removeEventListener('pagehide', notifyPeer);
    };
  }, []);

  // Cross-tab call resolution. The backend rings EVERY tab the user has open;
  // a reject/accept in one tab is invisible to the others, so they'd ring on
  // until their own timeout. We listen for a sibling's 'call-resolved' and, if
  // THIS tab is merely ringing (incoming) for that same call, dismiss it too.
  // Guarded to 'incoming' so the tab actually handling the call (outgoing /
  // connecting / connected) is never torn down by a sibling — also keeps the
  // same-account two-tab "self-call" test from collapsing the caller tab.
  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return;
    const ch = new BroadcastChannel('messenger-call');
    callChannelRef.current = ch;
    ch.onmessage = (e: MessageEvent) => {
      const m = e.data;
      if (
        m?.type === 'call-resolved' && m.callId &&
        routeRef.current?.callId === m.callId &&
        statusRef.current === 'incoming'
      ) {
        cleanup(null, false, 'none'); // false: don't re-broadcast (avoid a ping-pong)
      }
    };
    return () => { ch.close(); callChannelRef.current = null; };
  }, [cleanup]);

  const openVerifiedInvite = useCallback((frame: any, authorized: CallSnapshot) => {
    if (
      !isAuthorizedRingingInvite(frame, authorized)
      || routeRef.current !== null
      || statusRef.current !== 'idle'
      || isOtherCallActive('oneToOne')
    ) return;

    storeSnapshot(authorized);
    routeRef.current = {
      callId: frame.call_id, conversationId: frame.conversation_id, peerUserId: frame.from_user_id,
    };
    unloadActionRef.current = null;
    callMetaRef.current = {
      callId: frame.call_id, peerUserId: frame.from_user_id, conversationId: frame.conversation_id,
      direction: 'incoming', ringStartedAt: Date.now(), answeredAt: null,
    };
    isCallerRef.current = false;
    answerAppliedRef.current = false;
    pendingOffer.current = frame.sdp;
    setMinimized(false);
    setCall({
      ...IDLE, status: 'incoming', callId: frame.call_id, conversationId: frame.conversation_id,
      peerUserId: frame.from_user_id, peerName: frame.from_name ?? frame.from_user_id,
      peerAvatarUrl: frame.from_avatar_url ?? null,
    });
    // If the invite carried no caller photo, resolve it from the conversation
    // and patch the already-authorized incoming surface.
    if (!frame.from_avatar_url && frame.conversation_id) {
      const callId = frame.call_id;
      const peerId = frame.from_user_id;
      void getConversation(frame.conversation_id).then((conv: any) => {
        const peer = conv?.members?.find((m: any) => m.id === peerId || m.user_id === peerId);
        const url = peer?.avatar_url || (conv?.type !== 'dm' ? conv?.avatar_url : null) || null;
        if (url) setCall((current) => current.callId === callId ? { ...current, peerAvatarUrl: url } : current);
      }).catch(() => { /* best-effort */ });
    }
  }, [storeSnapshot]);

  const declineInviteWithoutOpening = useCallback((frame: any, authorized: CallSnapshot) => {
    if (!isAuthorizedRingingInvite(frame, authorized)) return;
    const eventId = createCallEventId('call-decline');
    void actOnCall(frame.call_id, {
      action: 'decline', eventId, expectedVersion: authorized.version,
    }).catch(() => { /* stale or unauthorized invites never create UI */ });
  }, []);

  const recoverCalls = useCallback((): Promise<void> => {
    if (recoveryInFlightRef.current) return recoveryInFlightRef.current;

    const recovery = (async () => {
      try {
        const { calls } = await syncCalls();
        const route = routeRef.current;
        if (route) {
          const routed = calls.find((item) => item.callId === route.callId);
          if (routed) reconcileSnapshot(routed);
          return;
        }
        if (statusRef.current !== 'idle') return;

        // Android wakes the app with an opaque pointer only. Re-authorize the
        // call and fetch the short-lived offer through the authenticated API;
        // native code never supplies SDP or ICE material.
        const nativePointer = await getPendingNativeCall();
        if (nativePointer) {
          const authorized = calls.find(item => item.callId === nativePointer.callId);
          if (authorized?.status === 'ringing' && authorized.mediaTopology === 'p2p' && !authorized.isGroup) {
            try {
              const offer = await getP2pOffer(authorized.callId);
              if (offer.call_id === authorized.callId && offer.media === 'audio') {
                nativeCallIdRef.current = nativePointer.callId;
                openVerifiedInvite(offer, authorized);
                if (routeRef.current) return;
              }
            } catch {
              // The offer may have expired; the next push/socket wake retries.
            }
          } else {
            await clearNativeIncomingCall(nativePointer.callId);
          }
        }

        // Offer recovery authorizes the current viewer as the callee and gives
        // us the caller identity/SDP needed to rebuild the existing answer UI.
        const ringingP2p = calls.filter((item) => (
          item.status === 'ringing' && item.mediaTopology === 'p2p' && !item.isGroup
        ));
        for (const authorized of ringingP2p) {
          try {
            const offer = await getP2pOffer(authorized.callId);
            if (routeRef.current || statusRef.current !== 'idle') return;
            if (isDndQuiet) declineInviteWithoutOpening(offer, authorized);
            else openVerifiedInvite(offer, authorized);
            if (routeRef.current) return;
          } catch {
            // The initiator cannot recover its own offer; try another ringing
            // P2P session that may be an authorized incoming call.
          }
        }

        const latestP2p = calls.find((item) => item.mediaTopology === 'p2p' && !item.isGroup);
        if (latestP2p) reconcileSnapshot(latestP2p);
      } catch {
        // The next socket open/state frame retries recovery.
      } finally {
        recoveryInFlightRef.current = null;
      }
    })();
    recoveryInFlightRef.current = recovery;
    return recovery;
  }, [declineInviteWithoutOpening, isDndQuiet, openVerifiedInvite, reconcileSnapshot]);

  useEffect(() => {
    void recoverCalls();
  }, [recoverCalls]);

  const resolveLegacyTerminalFrame = useCallback((type: string) => {
    const route = routeRef.current;
    if (!route) return;
    const eventId = createCallEventId('call-legacy-terminal');

    const isTerminal = (candidate: CallSnapshot) => isTerminalSnapshot(candidate.status);
    const actionFor = (candidate: CallSnapshot): CallLifecycleAction | null => {
      if (candidate.status === 'ringing') return isCallerRef.current ? 'cancel' : 'decline';
      if (candidate.status === 'active') return 'end';
      return null;
    };
    const finish = (terminal: CallSnapshot) => {
      if (terminal.callId !== route.callId || !isTerminal(terminal)) return;
      reconcileSnapshot(terminal);
      // reconcileSnapshot normally performs cleanup. If this action's own
      // event is still pending, it deliberately defers cleanup to this owner.
      if (routeRef.current?.callId !== route.callId) return;
      const meta = callMetaRef.current;
      if (type === 'call-busy') cleanup('User busy', true, 'busy');
      else if (type === 'call-reject') cleanup('Call declined', true, 'declined');
      else if (type === 'call-cancel') {
        cleanup('Call cancelled', true, meta?.direction === 'incoming' ? 'missed' : 'cancelled');
      } else {
        cleanup('Call ended', true, meta?.answeredAt ? 'completed' : 'failed');
      }
    };

    void (async () => {
      try {
        let current = await getCallSnapshot(route.callId);
        if (routeRef.current?.callId !== route.callId || current.callId !== route.callId) return;
        if (isTerminal(current)) {
          finish(current);
          return;
        }

        let action = actionFor(current);
        if (!action) return;
        storeSnapshot(current);
        pendingActionRef.current = { callId: route.callId, eventId };
        try {
          const terminal = await actOnCall(route.callId, {
            action, eventId, expectedVersion: current.version,
          });
          finish(terminal);
          return;
        } catch (error: any) {
          if (error?.response?.status !== 409) return;
        }

        // State may have crossed ringing→active between the read and action.
        // Recompute the transition, but retain the idempotency event id.
        current = await getCallSnapshot(route.callId);
        if (routeRef.current?.callId !== route.callId || current.callId !== route.callId) return;
        if (isTerminal(current)) {
          finish(current);
          return;
        }
        action = actionFor(current);
        if (!action) return;
        storeSnapshot(current);
        const terminal = await actOnCall(route.callId, {
          action, eventId, expectedVersion: current.version,
        });
        finish(terminal);
      } catch {
        // A later authoritative call-state frame remains the cleanup source.
      } finally {
        if (pendingActionRef.current?.eventId === eventId) pendingActionRef.current = null;
      }
    })();
  }, [cleanup, reconcileSnapshot, storeSnapshot]);

  const handleSignal = useCallback((frame: any) => {
    const t = frame?.type;
    if (t === 'call-invite') {
      if (
        typeof frame.call_id !== 'string'
        || typeof frame.conversation_id !== 'string'
        || typeof frame.from_user_id !== 'string'
        || !frame.sdp
        || frame.sdp.type !== 'offer'
      ) return;
      // Local failsafe for a frame already in flight when DND was enabled (or
      // from an older server): never create a ring, native popup, or call UI.
      if (isDndQuiet) {
        const known = snapshotRef.current?.callId === frame.call_id ? snapshotRef.current : null;
        if (known) declineInviteWithoutOpening(frame, known);
        else void getCallSnapshot(frame.call_id)
          .then((authorized) => declineInviteWithoutOpening(frame, authorized))
          .catch(() => { /* unauthorized or stale */ });
        return;
      }
      // Already resolved this call id (declined/ended, possibly in another tab)
      // → ignore a duplicate/re-published invite so it can't re-ring.
      if (frame.call_id && declinedCallIdsRef.current.includes(frame.call_id)) return;
      const verifyAndOpen = (authorized: CallSnapshot) => {
        if (routeRef.current || statusRef.current !== 'idle' || isOtherCallActive('oneToOne')) {
          declineInviteWithoutOpening(frame, authorized);
          return;
        }
        openVerifiedInvite(frame, authorized);
      };
      const known = snapshotRef.current?.callId === frame.call_id ? snapshotRef.current : null;
      if (known) verifyAndOpen(known);
      else void getCallSnapshot(frame.call_id).then(verifyAndOpen).catch(() => { /* unauthorized or stale */ });
      return;
    }
    // Ignore frames that don't belong to the current call.
    if (!routeRef.current || frame.call_id !== routeRef.current.callId) return;

    if (t === 'call-answer') {
      if (!isCallerRef.current) {
        // Echo of THIS user answering in another session (the backend fans
        // the answer back to the answerer's other sockets): if we're a
        // still-ringing callee tab, dismiss silently — answered elsewhere,
        // not missed. The session that actually answered is past 'incoming'
        // and ignores its own echo here.
        if (statusRef.current === 'incoming') cleanup(null, true, 'none');
        return;
      }
      // Only the caller applies an answer, and only once (see refs above).
      if (answerAppliedRef.current) return;
      const applyAnswer = (authorized: CallSnapshot) => {
        if (authorized.callId !== frame.call_id || authorized.status !== 'active' || answerAppliedRef.current) return;
        storeSnapshot(authorized);
        answerAppliedRef.current = true;
        stopAllCallSounds();
        for (const candidate of localCandidatesRef.current) sendFrame('ice-candidate', { candidate });
        void peerRef.current?.setRemoteDescription(frame.sdp).then(flushCandidates);
        setCall((current) => current.status === 'outgoing' ? { ...current, status: 'connecting' } : current);
      };
      const known = snapshotRef.current?.callId === frame.call_id ? snapshotRef.current : null;
      if (known?.status === 'active') applyAnswer(known);
      else void getCallSnapshot(frame.call_id).then(applyAnswer).catch(() => { /* unauthorized/stale answer */ });
    } else if (t === 'ice-candidate') {
      if (peerRef.current) void peerRef.current.addIceCandidate(frame.candidate);
      else pendingCandidates.current.push(frame.candidate);
    } else if (t === 'call-reject') {
      resolveLegacyTerminalFrame(t);
    } else if (t === 'call-busy') {
      resolveLegacyTerminalFrame(t);
    } else if (t === 'call-cancel') {
      resolveLegacyTerminalFrame(t);
    } else if (t === 'call-end') {
      resolveLegacyTerminalFrame(t);
    }
  }, [
    flushCandidates, cleanup, isDndQuiet, openVerifiedInvite, declineInviteWithoutOpening,
    resolveLegacyTerminalFrame, sendFrame, storeSnapshot,
  ]);

  const apiValue: CallApi = {
    call, snapshot, minimized, startCall, accept, reject, hangup, toggleMute, toggleMinimize, minimize,
    handleSignal, handleCallState, recoverCalls, bindSend, bindEscalate,
  };

  return (
    <Ctx.Provider value={apiValue}>
      {children}
      {call.status !== 'idle' && (
        <CallScreen
          status={call.status}
          peerName={call.peerName}
          peerAvatarUrl={call.peerAvatarUrl}
          muted={call.muted}
          startedAt={call.startedAt}
          endReason={call.endReason}
          minimized={minimized}
          remoteSpeaking={remoteSpeaking}
          onAccept={accept}
          onReject={reject}
          onToggleMute={toggleMute}
          onHangup={hangup}
          onToggleMinimize={toggleMinimize}
          onMinimize={minimize}
          onAddParticipant={() => setAddingOpen(true)}
        />
      )}
      {addingOpen && call.peerUserId && (
        <StartCallPicker
          multi
          title="Add to call"
          excludeUserIds={[call.peerUserId]}
          onClose={() => setAddingOpen(false)}
          onPickMany={(users) => {
            setAddingOpen(false);
            escalateRef.current?.({ peerUserId: call.peerUserId!, peerName: call.peerName, newUserIds: users.map(u => u.user_id) });
          }}
        />
      )}
      {/* Remote audio sink — hidden, autoplay. */}
      <audio ref={remoteAudioRef} autoPlay />
    </Ctx.Provider>
  );
}

export function useCall(): CallApi {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useCall must be used within a CallProvider');
  return ctx;
}
